import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { Alert, Box, Button, ButtonGroup, Grid } from '@mui/material';

import { AxiosError } from 'axios';
import {
  DefaultNamesForPaymentMethodConfigTypes,
  DefaultShortNamesForPaymentMethodConfigTypes,
  PaymentMethodConfigTypes,
} from '../../model/PaymentMethodConfig';
import {
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
} from '../../stores/FetcherStores/PaymentMethodFetcher';
import { usePaymentMethodForm } from './fields/usePaymentMethodForm';
import useCompanyId from '../../hooks/useCompanyId';
import SharedPaymentMethodFieldGroup from './fields/SharedPaymentMethodFieldGroup';
import NetsFieldGroup from './fields/NetsFieldGroup';
import { NetsTerminalPaymentMethod } from '../../model/PaymentMethod';

interface NetsSettingsProps {
  onDeletePaymethod: () => void;
  onClose: () => void;
  paymentMethod?: NetsTerminalPaymentMethod;
  paymentMethodId?: string;
}

const NetsSettings = observer(
  ({ onDeletePaymethod, onClose, paymentMethod, paymentMethodId }: NetsSettingsProps) => {
    const { t } = useTranslation();
    const companyId = useCompanyId();
    const {
      mutateAsync: createPaymentMethod,
      isLoading: isCreating,
      error: createError,
    } = useCreatePaymentMethodMutation();

    const {
      mutateAsync: updatePaymentMethod,
      isLoading: isUpdating,
      error: updateError,
    } = useUpdatePaymentMethodMutation();

    const isLoading = isCreating || isUpdating;
    const error = createError || updateError;

    const form = usePaymentMethodForm({
      defaultValues: {
        baseFields: {
          name:
            paymentMethod?.name ??
            DefaultNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.NETS_TERMINAL],
          shortName:
            paymentMethod?.shortName ??
            DefaultShortNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.NETS_TERMINAL],
          active: paymentMethod?.active ?? true,
          bookkeepingAccount: paymentMethod?.bookkeepingAccount ?? '',
        },
        netsFields: {
          terminalId: paymentMethod?.terminalId ?? '',
          username: paymentMethod?.username ?? '',
          password: paymentMethod?.password ?? '',
          boughtViaEnklakassan: paymentMethod?.boughtViaEnklakassan ?? false,
          acquirer: paymentMethod?.acquirer ?? '',
          acquirerNumber: paymentMethod?.acquirerNumber ?? '',
          mccCode: paymentMethod?.mccCode ?? '',
        },
      },
      onSubmit: async ({ value }) => {
        const newPaymentMethod: Omit<NetsTerminalPaymentMethod, 'id'> = {
          type: PaymentMethodConfigTypes.NETS_TERMINAL,
          name: value.baseFields.name,
          shortName: value.baseFields.shortName,
          active: value.baseFields.active,
          bookkeepingAccount: value.baseFields.bookkeepingAccount,
          terminalId: value.netsFields.terminalId,
          username: value.netsFields.username,
          password: value.netsFields.password,
          boughtViaEnklakassan: value.netsFields.boughtViaEnklakassan,
          acquirer: value.netsFields.acquirer,
          acquirerNumber: value.netsFields.acquirerNumber,
          mccCode: value.netsFields.mccCode,
        };
        if (paymentMethodId) {
          await updatePaymentMethod(companyId, paymentMethodId, newPaymentMethod)
            .then(() => {
              onClose();
            })
            .catch(() => {});
        } else {
          await createPaymentMethod(companyId, newPaymentMethod)
            .then(() => {
              onClose();
            })
            .catch(() => {});
        }
      },
    });

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={2} sx={{ marginTop: 0, marginBottom: 0 }}>
          {error && (
            <Alert severity="error">
              {error instanceof AxiosError
                ? error?.response?.data?.name || error?.message
                : error?.message}
            </Alert>
          )}
          <form.AppForm>
            <SharedPaymentMethodFieldGroup form={form} fields="baseFields" />
            <NetsFieldGroup form={form} fields="netsFields" />
          </form.AppForm>
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 'auto',
            paddingBottom: 2,
          }}
        >
          <ButtonGroup sx={{ display: 'flex', gap: 2 }}>
            {paymentMethodId && (
              <Button
                variant="contained"
                color="error"
                onClick={onDeletePaymethod}
                disabled={isLoading}
              >
                {t('paymethodScreen.removePaymethod')}
              </Button>
            )}
            <Button onClick={() => form.handleSubmit()} variant="contained" disabled={isLoading}>
              {!paymentMethodId ? t('paymethodScreen.add') : t('paymethodScreen.save')}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    );
  },
);

export default NetsSettings;
